import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRight } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";

interface CampaignElsewhereNoticeProps {
	campaignName: string;
	listName: string;
	/** Route to the Inbox/Outbox the campaigning row actually lives in. */
	to: string;
	/** Web-app origin, so the Chrome extension's panel links out to the app rather than to LinkedIn. */
	webAppUrl?: string;
}

/**
 * Explains that this lead is already being contacted — from a **different** list (PREC-3906).
 *
 * The lead's red `IN_ACTIVE_CAMPAIGN` dot is computed prospect-wide, so a lead can be a plain new
 * lead on the list you are looking at and mid-campaign on another one. Nothing in the panel used to
 * say so, which read as the dot being wrong. The swatch is deliberately the same red as that dot:
 * it names the thing the user just hovered instead of introducing a second vocabulary.
 *
 * The row's own actions stay untouched above this — on this list the lead genuinely is new, and
 * pausing belongs where the campaign runs.
 */
export const CampaignElsewhereNotice: FunctionComponent<CampaignElsewhereNoticeProps> = ({
	campaignName,
	listName,
	to,
	webAppUrl,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className="mt-1.5 flex items-center gap-2 rounded-[10px] border border-gray-200 bg-gray-50 px-2.5 py-2"
			data-testid="lead-panel-campaign-elsewhere"
		>
			<span className="h-2 w-2 shrink-0 rounded-full bg-status-error" />
			<div className="min-w-0 flex-1">
				<div className="truncate text-xs font-semibold text-text">
					{t("contacts.leadPanel.actions.campaign.elsewhere.label")}
				</div>
				<div className="truncate text-xs text-text-meta">
					{t("contacts.leadPanel.actions.campaign.elsewhere.detail", {
						campaign: campaignName,
						list: listName,
					})}
				</div>
			</div>
			<a
				className="flex shrink-0 cursor-pointer items-center gap-1 text-xs font-semibold text-blue-action hover:underline"
				data-testid="lead-panel-campaign-elsewhere-link"
				href={`${webAppUrl ?? ""}${to}`}
				rel="noreferrer"
				target="_blank"
			>
				{t("contacts.leadPanel.actions.campaign.elsewhere.link")}
				<Icon className="w-3" icon={faArrowUpRight} />
			</a>
		</div>
	);
};
